import { SelectorType } from "../../../components/shared/Selector/Selector";
import Selector from "../../../components/shared/Selector/Selector";
import styles from './CategorySelection.module.scss';
import BtButton from "../../bt-button/bt-button";
import { TrashIcon } from "../../../components/svgs";
import StyledCheckbox from "../../../components/shared/StyledCheckbox/StyledCheckbox";
import SearchBarWithActions from '../../../components/shared/SearchBarWithActions/SearchBarWithActions';
import { ServiceTemplate } from "../../../services/service-template";
import { useState, useEffect } from 'react';
import { uniqueId } from "lodash";
import Badge from "../../../components/shared/Badge/Badge";
import { useTranslation } from "react-i18next";

export type SelectedCategory = {
  id: string,
  mainCategory: SelectorType & {subAreas: SelectorType[]} | null,
  subCategory: SelectorType | null,
};

type Template = {
  _id: string;
  name: string;
  categories: string[];
  orgId: string;
};

type Props = {
  areas: (SelectorType & {subAreas: SelectorType[], _id: string})[],
  currentProjectAreas: {areaId: { name: string, slug: string }}[],
  selectedCategories: SelectedCategory[],
  setSelectedCategories: (fn: (prevState: SelectedCategory[]) => SelectedCategory[]) => void,
}

const ShowListCategory = ({ 
  areas, 
  currentProjectAreas, 
  selectedCategories,
  setSelectedCategories,
}: Props) => {
  const [t] = useTranslation('projects', { keyPrefix: 'categorySelection' });
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateName, setTemplateName] = useState<SelectorType | null>(null);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const templates = await ServiceTemplate.getTemplates();
      setTemplates(templates);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleSaveTemplate = async (name: string, categories: string[]) => {
    try {
      await ServiceTemplate.saveTemplate(name, categories);
      await loadTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await ServiceTemplate.deleteTemplate(templateId);
      await loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const isInProjectAreas = (areaValue: string) => 
    currentProjectAreas.some(projectArea => projectArea.areaId.slug === areaValue);

  const handleCategoryChange = (area: SelectorType & {subAreas: SelectorType[]}, checked: boolean) => {
    setSelectedCategories(prevState => {
      const newState = prevState.filter(cat => cat.mainCategory !== null);
      
      if (checked) {
        const newCategory = {
          id: uniqueId(),
          mainCategory: area,
          subCategory: null
        };
        newState.push(newCategory);
      } else {
        const index = newState.findIndex(cat => cat.mainCategory?.value === area.value);
        if (index !== -1) {
          newState.splice(index, 1);
        }
      }
      return newState;
    });
  };

  const isCategorySelected = (area: SelectorType & {subAreas: SelectorType[]}) => {
    return selectedCategories.some(cat => cat.mainCategory?.value === area.value);
  };

  return (
    <div className={styles.container}>
      <div className={styles.modalContent}>
        <span className={styles.label}>{t('template', 'Snabbval')}:</span>
        <Selector
          className={styles.areaSelector}
          value={templateName}
          isClearable
          onChange={(value) => {
            setTemplateName(value);
            if (value && templates.some(t => t._id === value.value)) {
              const selectedTemplate = templates.find(t => t._id === value.value);
              if (selectedTemplate) {
                setSelectedCategories(() => []);
                const newCategories = selectedTemplate.categories
                  .map(categoryValue => {
                    const area = areas.find(a => a.value === categoryValue);
                    if (area) {
                      return {
                        id: uniqueId(),
                        mainCategory: area,
                        subCategory: null
                      } as SelectedCategory;
                    }
                    return null;
                  })
                  .filter((cat): cat is SelectedCategory => cat !== null);
                
                setSelectedCategories(() => newCategories);
              }
            }
          }}
          placeholder={t('selectOrCreateCategory', 'Välj eller skapa')}
          isCreatable
          options={templates.map(t => ({ label: t.name, value: t._id }))}
        />
        <BtButton
          color='white'
          size='small'
          onClick={async () => {
            if (templateName) {
              try {
                const categories = areas
                  .filter(area => isCategorySelected(area))
                  .map(area => area.value);
                
                await handleSaveTemplate(templateName.label, categories);
                setTemplateName(null);
              } catch (error) {
                console.error('Failed to save template:', error);
              }
            }
          }}
          disabled={!templateName}
          style={{ fontSize: '14px' }}
        >
          {t('saveTemplate', 'Spara snabbval')}
        </BtButton>
        <BtButton
          color='white'
          size='small'
          onClick={async () => {
            if (templateName) {
              try {
                await handleDeleteTemplate(templateName.value);
                setTemplateName(null);
              } catch (error) {
                console.error('Failed to delete template:', error);
              }
            } else {
              setTemplateName(null);
            }
          }}
          leftIcon={<TrashIcon size='14px' color='var(--gray-700)' />}
          style={{ marginRight: '40px', fontSize: '14px' }}
        >
          {t('deleteTemplate', 'Ta bort snabbval')}
        </BtButton>
      </div>
      <div className={styles.searchContainer}>
        <SearchBarWithActions
          className={styles.searchBar}
          value={searchValue}
          onChange={setSearchValue}
          style={{ width: '100%' }}
        >
          <></>
        </SearchBarWithActions>
      </div>
      <div className={styles.categoryList}>
        {areas
          .filter(area => area.label.toLowerCase().includes(searchValue.toLowerCase()))
          .map((area: SelectorType & {subAreas: SelectorType[]}) => {
            const isSelected = isCategorySelected(area);

            return (
              <div key={area.value} className={styles.categoryGroup}>
                <div className={`${styles.mainCategory}`} onClick={() => {
                  handleCategoryChange(area, !isSelected);
                }}>
                  <label className={styles.categoryLabel}>
                    <StyledCheckbox
                      checked={isSelected}
                      className={styles.categoryCheckbox}
                    />
                    <span>{area.label}</span>
                  </label>

                  {isInProjectAreas(area.value) && (
                    <Badge>{t('categoryAlreadyExists', 'Finns redan')}</Badge>
                  )}
                </div>
              </div>
            );
          })}
        {searchValue && areas.filter(area => area.label.toLowerCase().includes(searchValue.toLowerCase())).length === 0 && (
          <div className={styles.noResults}>
            {t('noResults', 'Kunde inte hitta något resultat')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowListCategory; 